import React from 'react'
import { Grid, Typography } from '@material-ui/core'
import { Formik, Form } from 'formik'
import {
  weeklyWorkloadHours,
  paroIncomeStatus,
  totalFreelanceExperience,
  projectTypes,
  communicationMethods,
  contactTimePreferences,
  weeklyHoursMapToMonthlyHours,
} from '../../../shared/constants/workPreferences'
import workPrefsValidationSchema from './validationSchema'
import { useOnboarding } from '../../../contexts/OnboardingContext'
import ValidatedInputAdornmentTextField from '../../../_global/ValidatedInputAdornmentTextField'
import ValidatedRadioGroup from '../../../_global/ValidatedRadioGroup'
import { useMutation } from '@apollo/react-hooks'
import createOrUpdateWorkPreferenceMutation from '../../../apolloClient/mutations/createOrUpdateWorkPreference'
import updateFreelancerHoursAvailableMutation from '../../../apolloClient/mutations/updateFreelancerHoursAvailable'
import WORK_PREFERENCES_QUERY from '../../../apolloClient/queries/getWorkPreferences'
import { useAuth0 } from '../../../contexts/Auth0Context'
import FormActions from '../../../_global/FormActions'
import ValidatedDropdown from '../../../_global/ValidatedDropdown'

const RADIO_FIELD_YES_NO_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
]

const WorkPreferences = ({ initialValues }) => {
  const { logout } = useAuth0()
  const { back, currentExpert, formRef, next: nextStep, step } = useOnboarding()
  const [workPreferenceMutation] = useMutation(createOrUpdateWorkPreferenceMutation)
  const [hoursAvailableMutation] = useMutation(updateFreelancerHoursAvailableMutation)

  const formatBooleanValues = (value) => {
    return value === 'true' ? true : false
  }

  const buildContactInformationMutationInput = (values) => {
    return {
      expertId: initialValues.expertId,
      input: {
        workPreference: {
          id: initialValues.id,
          contactStylePreference: values.contactStylePreference === '' ? null : values.contactStylePreference,
          contactTimePreference: values.contactTimePreference === '' ? null : values.contactTimePreference,
          isOpenToOnsite: values.isOpenToOnsite === '' ? null : formatBooleanValues(values.isOpenToOnsite),
          intendedMonthlyIncome: values.intendedMonthlyIncome === '' ? null : values.intendedMonthlyIncome,
          projectType: values.projectType === '' ? null : values.projectType,
          weeklyWorkloadHours: values.weeklyWorkloadHours === '' ? null : values.weeklyWorkloadHours,
          totalFreelanceExperience: values.totalFreelanceExperience === '' ? null : values.totalFreelanceExperience,
        },
        expert: {
          paroIncomeStatus: values.paroIncomeStatus === '' ? null : values.paroIncomeStatus,
          hasCurrentClients: values.hasCurrentClients === '' ? null : formatBooleanValues(values.hasCurrentClients),
          legacyFreelancerId: initialValues.legacyFreelancerId,
        },
      },
    }
  }

  const buildHoursAvailablePayload = ({ weeklyWorkloadHours }) => {
    const monthlyAvailableHours = weeklyHoursMapToMonthlyHours[weeklyWorkloadHours] || 0
    return {
      freelancerId: initialValues.legacyFreelancerId,
      hoursAvailable: monthlyAvailableHours,
    }
  }

  const handleSubmit = async (values) => {
    values.intendedMonthlyIncome = values.intendedMonthlyIncome ? Number(values.intendedMonthlyIncome) : null

    await workPreferenceMutation({
      variables: buildContactInformationMutationInput(values),
      refetchQueries: [{ query: WORK_PREFERENCES_QUERY, variables: { id: currentExpert.id } }],
    })
    await hoursAvailableMutation({
      variables: buildHoursAvailablePayload(values),
    }).then((result) => {
      if (values.isSaveAndExit) {
        logout()
      } else {
        if (step !== 7) {
          nextStep()
        }
      }
    })
  }

  return (
    <Formik
      validateOnMount={true}
      initialValues={initialValues}
      validationSchema={workPrefsValidationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
    >
      {(props) => {
        const { isSubmitting, isValid } = props

        return (
          <Form autoComplete="off">
            <Grid container spacing={4}>
              <Grid item>
                <Typography variant="body2" gutterBottom>
                  Weekly hourly goal
                </Typography>
                <ValidatedDropdown
                  name="weeklyWorkloadHours"
                  items={weeklyWorkloadHours}
                  value={props.values.weeklyWorkloadHours || ''}
                  placeholder="Select"
                />
              </Grid>
              <Grid item>
                <Typography variant="body2" gutterBottom>
                  What is your monthly income goal with Paro?
                </Typography>
                <ValidatedInputAdornmentTextField
                  name="intendedMonthlyIncome"
                  onChange={props.handleChange}
                  type="number"
                  iconAdornmentPosition="start"
                  iconAdornmentContent="$"
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  Please select which situation fits you best:
                </Typography>
                <ValidatedRadioGroup disabled={false} name="paroIncomeStatus" options={paroIncomeStatus} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  How long have you been freelancing in financing/accounting?
                </Typography>
                <ValidatedRadioGroup
                  disabled={false}
                  name="totalFreelanceExperience"
                  options={totalFreelanceExperience}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  Do you have an existing book of business?
                </Typography>
                <ValidatedRadioGroup disabled={false} options={RADIO_FIELD_YES_NO_OPTIONS} name="hasCurrentClients" />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  What type of projects are you willing to work on?
                </Typography>
                <ValidatedRadioGroup disabled={false} options={projectTypes} name="projectType" />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  When are you available to talk to clients?
                </Typography>
                <ValidatedRadioGroup disabled={false} options={contactTimePreferences} name="contactTimePreference" />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  Are you willing to go onsite? <i>Note: the vast majority of engagements are remote!</i>
                </Typography>
                <ValidatedRadioGroup disabled={false} options={RADIO_FIELD_YES_NO_OPTIONS} name="isOpenToOnsite" />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  What is the best method of communication?
                </Typography>
                <ValidatedRadioGroup disabled={false} options={communicationMethods} name="contactStylePreference" />
              </Grid>
            </Grid>
            {step !== 7 && <FormActions step={step} back={back} isValid={isValid} isSubmitting={isSubmitting} />}
          </Form>
        )
      }}
    </Formik>
  )
}

export default WorkPreferences
